import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { DMS_CUSTOM_QUERY_HISTORY_STATUSES } from "models";
import "modules/DMSv3/CustomQueries/components/QueryResult/QueryResult.scss";
import { autoColumnWidth } from "modules/DMSv3/CustomQueries/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { DMSCustomQueryHistory } from "models";

type QueryResultProps = {
  query: DMSCustomQueryHistory | null;
  isLoading: boolean;
};

type ResultRow = Record<string, unknown>;

const parseResult = (result: string | null): ResultRow[] => {
  if (!result) return [];

  try {
    const parsed = JSON.parse(result);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatCellValue = (value: unknown) => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "NULL";
  return String(value);
};

const columnHelper = createColumnHelper<ResultRow>();

type ResultTableProps = {
  rows: ResultRow[];
  isLoading: boolean;
};

const ResultTable = ({ rows, isLoading }: ResultTableProps) => {
  const columns = useMemo(() => {
    if (!rows.length) return [];

    return Object.keys(rows[0]).map((columnKey, idx) =>
      columnHelper.accessor(row => row, {
        id: columnKey || `id-${idx}`,
        size: autoColumnWidth(rows, columnKey, columnKey),
        header: () => <span>{columnKey || '""'}</span>,
        cell: ({ row: { original } }) => <span>{formatCellValue(original[columnKey])}</span>
      })
    );
  }, [rows]);

  return <ReactTable columns={columns} data={rows} isFetching={isLoading} />;
};

export const QueryResult = ({ query, isLoading }: QueryResultProps) => {
  const { t } = useTranslation();

  const getContent = () => {
    if (!query) return <p>{t("v8_submit_query", "Submit a query or select one in the query history")}</p>;

    if (query.status === DMS_CUSTOM_QUERY_HISTORY_STATUSES.Pending)
      return (
        <p>
          {t("v8_query_will_run", "Query will run at approximately")}: {formatDate(query.estimated_execution, DATE_FORMATS_FNS.yearMonthDatehoursMinutes)}
        </p>
      );

    if (query.status === DMS_CUSTOM_QUERY_HISTORY_STATUSES.Error)
      return (
        <p>
          {t("v8_query_failed_to_run", "Query failed to run")}: {query.result}
        </p>
      );

    return (
      <>
        <div className="query-result-header">{`${query.id} - ${query.name}`}</div>
        <ResultTable rows={parseResult(query.result)} isLoading={isLoading} />
      </>
    );
  };

  return <div className="QueryResult">{getContent()}</div>;
};
